
import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Expense } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { FileText, Receipt, Eye, Pencil, Trash } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import ReceiptUpload from "./ReceiptUpload";
import ExpenseReportDialog from "./ExpenseReportDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";

interface ExpenseListProps {
  expenses: Expense[];
  onReportStatusChange?: () => void;
}

const ExpenseList = ({ expenses, onReportStatusChange }: ExpenseListProps) => {
  const { organizationId } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedExpenses, setSelectedExpenses] = useState<Expense[]>([]);
  const [receiptUrls, setReceiptUrls] = useState<{ [key: number]: string }>({});
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Edit form state
  const [editDescription, setEditDescription] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editDate, setEditDate] = useState<Date | undefined>(undefined);
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editCurrency, setEditCurrency] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    };
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session?.user);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Improved query that fetches both receipts for expenses and expense reports
  const { data: receipts, refetch: refetchReceipts } = useQuery({
    queryKey: ["receipts", expenses.map(e => e.id)],
    queryFn: async () => {
      if (expenses.length === 0) return [];
      const { data, error } = await supabase
        .from("receipts")
        .select("*")
        .in("expense_id", expenses.map(e => e.id));
      
      if (error) throw error;
      return data;
    },
    enabled: expenses.length > 0,
  });

  // Get current user
  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return user;
    },
  });

  const { data: userRole } = useQuery({
    queryKey: ["user-role", organizationId, currentUser?.id],
    queryFn: async () => {
      if (!organizationId || !currentUser) return null;

      const { data, error } = await supabase
        .from("organization_members")
        .select("role")
        .eq("organization_id", organizationId)
        .eq("user_id", currentUser.id)
        .single();
      
      if (error) throw error;
      return data?.role;
    },
    enabled: !!organizationId && !!currentUser,
  });
  
  // Get categories for the edit form
  const { data: categories } = useQuery({
    queryKey: ["categories", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("organization_id", organizationId);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });

  // This effect resets selected expenses when the expenses list changes
  useEffect(() => {
    // Clear any selected expenses that are now part of a report
    setSelectedExpenses(prev => 
      prev.filter(selected => 
        !expenses.find(e => e.id === selected.id && e.expense_report)
      )
    );
  }, [expenses]);

  useEffect(() => {
    const generateSignedUrls = async () => {
      const urls: { [key: number]: string } = {};
      if (!receipts) return;

      for (const receipt of receipts) {
        const fileName = receipt.file_url.split('/').pop();
        if (!fileName) continue;

        const { data: { signedUrl }, error } = await supabase.storage
          .from('receipts')
          .createSignedUrl(fileName, 3600); // URL valid for 1 hour

        if (!error && signedUrl) {
          urls[receipt.expense_id] = signedUrl;
        }
      }

      setReceiptUrls(urls);
    };

    if (receipts && receipts.length > 0) {
      generateSignedUrls();
    }
  }, [receipts]);

  const isManager = userRole === 'admin' || userRole === 'manager';

  // Helper function to ensure consistent date display without timezone issues
  const formatDate = (dateString: string): string => {
    try {
      // Parse the date string (yyyy-mm-dd) into a Date object
      // This approach avoids timezone issues by explicitly setting the date components
      const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
      
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        console.error("Invalid date format:", dateString);
        return dateString; // Return the original string if parsing fails
      }
      
      const date = new Date(year, month - 1, day);
      
      return format(date, "MMM d, yyyy");
    } catch (error) {
      console.error("Date formatting error:", error, dateString);
      return dateString;
    }
  };

  const handleExpenseSelect = (expense: Expense) => {
    // Don't allow selecting expenses that are already part of a report
    if (expense.expense_report) {
      toast({
        title: "Cannot select this expense",
        description: "This expense is already part of a report",
        variant: "destructive",
      });
      return;
    }

    // Also don't allow selecting report summary expenses
    if (expense.is_report_summary) {
      toast({
        title: "Cannot select this expense",
        description: "This is a summary of an expense report",
        variant: "destructive",
      });
      return;
    }

    setSelectedExpenses(prev => {
      const isSelected = prev.find(e => e.id === expense.id);
      if (isSelected) {
        return prev.filter(e => e.id !== expense.id);
      }
      return [...prev, expense];
    });
  };

  const handleReportCreated = () => {
    setSelectedExpenses([]);
    toast({
      title: "Success",
      description: "Expense report created successfully",
    });
  };

  const getReceiptForExpense = (expenseId: number) => {
    return receipts?.find(r => r.expense_id === expenseId);
  };

  const handleUploadComplete = () => {
    refetchReceipts();
    toast({
      title: "Success",
      description: "Receipt uploaded successfully",
    });
  };

  // This function is still needed for when the status of a report changes
  const handleReportStatusChange = () => {
    console.log("Report status changed, expenses should refresh");
    if (onReportStatusChange) {
      onReportStatusChange();
    }
  };

  // Navigate to expense report details page
  const navigateToExpenseReport = (reportId: string) => {
    if (organizationId) {
      navigate(`/app/${organizationId}/expense-reports/${reportId}`);
    }
  };

  // For editing an expense
  const handleEditExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    // Initialize form with expense data
    setEditDescription(expense.description);
    setEditAmount(expense.amount.toString());
    
    // Parse date string to Date object
    const [year, month, day] = expense.date.split('-').map(num => parseInt(num, 10));
    setEditDate(new Date(year, month - 1, day));
    
    setEditCategoryId(expense.category_id ? expense.category_id.toString() : "");
    setEditCurrency(expense.currency);
    setIsEditSheetOpen(true);
  };

  // Expense update function
  const handleUpdateExpense = async () => {
    if (!selectedExpense || !editDescription || !editAmount) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from("expenses")
        .update({
          description: editDescription,
          amount: parseFloat(editAmount),
          date: editDate ? format(editDate, 'yyyy-MM-dd') : selectedExpense.date,
          category_id: editCategoryId ? parseInt(editCategoryId) : null,
          currency: editCurrency
        })
        .eq('id', selectedExpense.id);
      
      if (error) throw error;
      
      setIsEditSheetOpen(false);
      setSelectedExpense(null);
      
      toast({
        title: "Expense updated",
        description: "The expense has been successfully updated",
      });
      
      // Refresh expenses list
      if (onReportStatusChange) {
        onReportStatusChange();
      }
    } catch (error) {
      console.error("Error updating expense:", error);
      toast({
        title: "Error",
        description: "Failed to update expense. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter out regular expenses and report summary expenses
  const regularExpenses = expenses.filter(e => !e.is_report_summary);
  const reportSummaryExpenses = expenses.filter(e => e.is_report_summary);
  
  // Don't render if user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-center text-gray-500">
          <p>Please log in to view expenses</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Expenses</h2>
        {selectedExpenses.length > 0 && organizationId && (
          <ExpenseReportDialog
            organizationId={organizationId}
            expenses={selectedExpenses}
            onReportCreated={handleReportCreated}
          />
        )}
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedExpenses.length === regularExpenses.filter(e => !e.expense_report).length && regularExpenses.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      // Filter out expenses that are already part of a report
                      const availableExpenses = regularExpenses.filter(e => !e.expense_report);
                      setSelectedExpenses(availableExpenses);
                    } else {
                      setSelectedExpenses([]);
                    }
                  }}
                />
              </TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Description</TableHead>
              {!isMobile && <TableHead>Category</TableHead>}
              <TableHead>Amount</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 5 : 6} className="text-center py-8 text-gray-500">
                  No expenses found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* First show report summary expenses at the top */}
                {reportSummaryExpenses.map((expense) => (
                  <TableRow 
                    key={expense.id}
                    className="bg-blue-50"
                  >
                    <TableCell>
                      <Checkbox
                        checked={false}
                        onCheckedChange={() => handleExpenseSelect(expense)}
                        disabled={true}
                      />
                    </TableCell>
                    <TableCell>{formatDate(expense.date)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{expense.description}</div>
                      {expense.expense_report && (
                        <div className="text-xs text-blue-600 mt-1">
                          Report ID: {expense.expense_report.id.substring(0, 8)}... ({expense.expense_report.status})
                        </div>
                      )}
                    </TableCell>
                    {!isMobile && <TableCell>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                        {expense.categories?.name || "Expense Report"}
                      </span>
                    </TableCell>}
                    <TableCell className="font-semibold">
                      {expense.currency} {parseFloat(expense.amount.toString()).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (expense.expense_report?.id) {
                            navigateToExpenseReport(expense.expense_report.id);
                          }
                        }}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        {!isMobile && "Expense Report"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Then show regular expenses */}
                {regularExpenses.map((expense) => (
                  <TableRow 
                    key={expense.id}
                    className={expense.expense_report ? 
                      "bg-gray-50 opacity-80" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedExpenses.some(e => e.id === expense.id)}
                        onCheckedChange={() => handleExpenseSelect(expense)}
                        disabled={!!expense.expense_report}
                      />
                    </TableCell>
                    <TableCell>{formatDate(expense.date)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{expense.description}</span>
                        {isMobile && expense.categories?.name && (
                          <span className="text-xs text-gray-500 mt-0.5">
                            {expense.categories.name}
                          </span>
                        )}
                        {expense.expense_report && (
                          <div className="text-xs text-gray-500 mt-1">
                            Report: {expense.expense_report.id.substring(0, 8)}... ({expense.expense_report.status})
                          </div>
                        )}
                      </div>
                    </TableCell>
                    {!isMobile && <TableCell>{expense.categories?.name}</TableCell>}
                    <TableCell className="font-medium">
                      {expense.currency} {parseFloat(expense.amount.toString()).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-1">
                        {expense.expense_report ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className={isMobile ? "p-2 h-8 w-8" : ""}
                            onClick={() => {
                              if (expense.expense_report?.id) {
                                navigateToExpenseReport(expense.expense_report.id);
                              }
                            }}
                          >
                            <FileText className="h-4 w-4" />
                            {!isMobile && <span className="ml-2">View Report</span>}
                          </Button>
                        ) : getReceiptForExpense(expense.id) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className={isMobile ? "p-2 h-8 w-8" : ""}
                            asChild
                          >
                            <a
                              href={receiptUrls[expense.id]}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Eye className="h-4 w-4" />
                              {!isMobile && <span className="ml-2">View Receipt</span>}
                            </a>
                          </Button>
                        ) : (
                          <ReceiptUpload
                            expenseId={Number(expense.id)}
                            organizationId={organizationId || ''}
                            onUploadComplete={handleUploadComplete}
                            compact={isMobile}
                          />
                        )}
                        
                        {!expense.expense_report && (
                          <Button
                            variant="outline"
                            size="sm"
                            className={isMobile ? "p-2 h-8 w-8" : ""}
                            onClick={() => handleEditExpense(expense)}
                          >
                            <Pencil className="h-4 w-4" />
                            {!isMobile && <span className="ml-2">Edit</span>}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Edit Expense Sheet for Mobile */}
      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent side="bottom" className="ios-sheet">
          <SheetHeader>
            <SheetTitle>Edit Expense</SheetTitle>
            <SheetDescription>
              Make changes to your expense here.
            </SheetDescription>
          </SheetHeader>
          
          {selectedExpense && (
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="What was this expense for?"
                  className="ios-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-amount">Amount</Label>
                <div className="flex gap-2">
                  <Select
                    value={editCurrency}
                    onValueChange={setEditCurrency}
                  >
                    <SelectTrigger className="w-24 ios-input">
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="CAD">CAD</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="edit-amount"
                    type="number"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    placeholder="0.00"
                    className="ios-input flex-1"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-date">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="edit-date"
                      variant="outline"
                      className="ios-date-trigger w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editDate ? format(editDate, "PPP") : "Select a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={editDate}
                      onSelect={(newDate) => newDate && setEditDate(newDate)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Select
                  value={editCategoryId}
                  onValueChange={setEditCategoryId}
                >
                  <SelectTrigger id="edit-category" className="ios-input">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.map((category) => (
                      <SelectItem key={category.id} value={String(category.id)}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <SheetFooter>
                <Button 
                  onClick={handleUpdateExpense} 
                  className="w-full mt-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </SheetFooter>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ExpenseList;
